/**
 * MLS-MAILAWAY-ALIGN-001 — Tax-style MLS Mail-Away modal: request list with
 * status badges + timeline, cycle history, Resend, Response Received,
 * Cancel-with-reason and attachment viewer. MLS-scoped endpoints only; the
 * parent order status is never touched (MLS completion guard unchanged).
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, Plus, Building2, CheckCircle, RotateCcw, XCircle, Paperclip, ChevronDown, ChevronUp, Clock, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { MailAwayStatusBadge } from '../mailaway/shared/MailAwayStatusBadge';
import { MailAwayResponseModal } from '../mailaway/shared/MailAwayResponseModal';
import { MailAwayCancelModal } from '../mailaway/shared/MailAwayCancelModal';
import { MailAwayAttachmentViewer } from '../mailaway/shared/MailAwayAttachmentViewer';
import { MAIL_AWAY_ACTIVE, getEffectiveMailAwayStatus } from '../mailaway/shared/mailAwayStatus';
import { MlsMailAwaySendModal, uploadMlsFiles, fmtFee } from './MlsMailAwaySendModal';
import { formatDateOnly, formatDateTimeShortET } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtDay = (s) => { if (!s) return null; try { return formatDateOnly(s); } catch { return s; } };
const fmtTs = (s) => { if (!s) return null; try { return formatDateTimeShortET(s); } catch { return s; } };

const ICON_TONE = {
  overdue: 'bg-red-50 text-red-600', response_received: 'bg-green-50 text-green-700', cancelled: 'bg-red-50 text-red-400',
  superseded: 'bg-slate-100 text-slate-400',
};

export const AttachmentChips = ({ attachments, onOpen, rid }) => {
  if (!attachments?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {attachments.map((a, i) => (
        <button key={a.file_id || i} type="button" onClick={() => onOpen(a)}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-slate-200 bg-white text-[11px] text-slate-600 hover:border-blue-300 hover:text-blue-700 transition-colors"
          data-testid={`mls-ma-attachment-${rid}-${i}`} title={a.kind === 'letter' ? 'View generated request letter' : 'View attachment'}>
          {a.kind === 'letter' ? <FileText className="w-3 h-3 text-blue-500" /> : <Paperclip className="w-3 h-3 text-slate-400" />}{a.filename || 'Attachment'}
          {a.kind === 'response' && <span className="text-green-600 font-semibold">· response</span>}
          {a.kind === 'letter' && <span className="text-blue-600 font-semibold">· letter</span>}
        </button>
      ))}
    </div>
  );
};

const Timeline = ({ req }) => (
  <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap items-center gap-x-1.5" data-testid={`mls-ma-timeline-${req.request_id}`}>
    <span>Sent {fmtDay(req.date_sent) || fmtTs(req.sent_at)}{req.sent_by_name ? ` by ${req.sent_by_name}` : ''}</span>
    {req.follow_up_date && <><span className="text-slate-300">→</span><span className={getEffectiveMailAwayStatus(req) === 'overdue' ? 'text-red-600 font-medium' : 'text-amber-700'}>Follow-up {fmtDay(req.follow_up_date)}</span></>}
    {req.received_at && <><span className="text-slate-300">→</span><span className="text-green-700">Received {fmtTs(req.received_at)} by {req.received_by_name}</span></>}
    {req.cancelled_at && <><span className="text-slate-300">→</span><span className="text-red-500">Cancelled {fmtTs(req.cancelled_at)} by {req.cancelled_by_name}</span></>}
    {req.superseded_at && <><span className="text-slate-300">→</span><span>Resent {fmtTs(req.superseded_at)}</span></>}
  </div>
);

const HistoryRow = ({ req, onOpenAttachment }) => (
  <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-slate-50 border border-slate-100" data-testid={`mls-ma-history-${req.request_id}`}>
    <Clock className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-semibold text-slate-600">Cycle {req.cycle_index || 1}</span>
        <span className="text-[10px] uppercase text-slate-400">{req.method}</span>
        <MailAwayStatusBadge request={req} />
      </div>
      <Timeline req={req} />
      {req.notes && <div className="text-[11px] text-slate-600 mt-0.5">{req.notes}</div>}
      <AttachmentChips attachments={req.attachments} onOpen={onOpenAttachment} rid={req.request_id} />
    </div>
  </div>
);

// MLS-MAILAWAY-EMAIL-AUTOMATION-004: client-email delivery status + retry (idempotent server-side)
const EmailStatus = ({ req, readOnly, onRetry }) => {
  const e = req.email;
  if (!e) return null;
  const tone = e.status === 'sent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : e.status === 'failed' ? 'bg-rose-50 text-rose-700 border-rose-200'
    : e.status === 'not_sent' ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-amber-50 text-amber-700 border-amber-200';
  const label = e.status === 'sent' ? `Email sent · ${e.recipient}`
    : e.status === 'failed' ? `Email failed · ${e.recipient}`
    : e.status === 'not_sent' ? `Email not sent · ${e.not_sent_reason && /allow-list/i.test(e.not_sent_reason) ? 'recipient not in activation allow-list' : 'live delivery not activated'} · ${e.recipient}` : `Email pending · ${e.recipient}`;
  return (
    <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid={`mls-ma-email-status-${req.request_id}`} data-email-status={e.status}>
      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium ${tone}`} title={e.sent_at ? `Sent ${fmtTs(e.sent_at)} · ${e.message_id || ''}` : e.error || ''}>
        <Mail className="w-3 h-3" />{label}{e.sent_at ? ` · ${fmtTs(e.sent_at)}` : ''}
      </span>
      {e.status === 'failed' && e.error && <span className="text-[11px] text-rose-700">{e.error}</span>}
      {!readOnly && e.status !== 'sent' && (
        <button type="button" className="text-[11px] font-semibold text-blue-700 hover:underline" data-testid={`mls-ma-email-retry-${req.request_id}`} onClick={() => onRetry(req)}>Retry email</button>
      )}
    </div>
  );
};

const RequestRow = ({ chain, readOnly, onReceive, onResend, onCancel, onOpenAttachment, onRetryEmail }) => {
  const [showHistory, setShowHistory] = useState(false);
  const req = chain[0];
  const history = chain.slice(1);
  const eff = getEffectiveMailAwayStatus(req);
  const active = MAIL_AWAY_ACTIVE.includes(req.status);
  const tone = ICON_TONE[eff] || 'bg-blue-50 text-blue-600';
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:bg-slate-50/50 transition-colors" data-testid={`mls-ma-row-${req.request_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={`p-1.5 rounded-md mt-0.5 shrink-0 ${tone}`}><Building2 size={14} /></div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-slate-800" data-testid={`mls-ma-dept-${req.request_id}`}>{req.department_name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">{req.section_label || req.section}</span>
              <span className="text-[10px] text-slate-500 uppercase">{req.method}</span>
              {fmtFee(req.mlc_fee) && <span className="text-[10px] px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold" data-testid={`mls-ma-fee-${req.request_id}`}>{fmtFee(req.mlc_fee)}</span>}
              {(req.cycle_index || 1) > 1 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide bg-violet-100 text-violet-800 border border-violet-200"
                  data-testid={`mls-ma-cycle-${req.request_id}`}>Cycle {req.cycle_index}</span>
              )}
              <MailAwayStatusBadge request={req} testId={`mls-ma-status-${req.request_id}`} />
            </div>
            <Timeline req={req} />
            {req.notes && <div className="mt-1 text-[11px] text-slate-600">{req.notes}</div>}
            {req.response_note && <div className="mt-0.5 text-[11px] text-green-800">Response: {req.response_note}</div>}
            {req.cancel_reason && <div className="mt-0.5 text-[11px] text-red-600">Reason: {req.cancel_reason}</div>}
            <AttachmentChips attachments={req.attachments} onOpen={onOpenAttachment} rid={req.request_id} />
            <EmailStatus req={req} readOnly={readOnly} onRetry={onRetryEmail} />
          </div>
        </div>
        {!readOnly && active && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[11px] text-green-700 border-green-300 hover:bg-green-50"
              data-testid={`mls-ma-receive-btn-${req.request_id}`} onClick={() => onReceive(req)}>
              <CheckCircle className="w-3 h-3 mr-1" /> Received
            </Button>
            <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[11px] text-blue-700 border-blue-300 hover:bg-blue-50"
              data-testid={`mls-ma-resend-btn-${req.request_id}`} onClick={() => onResend(req)}>
              <RotateCcw className="w-3 h-3 mr-1" /> Resend
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-slate-500 hover:text-red-600"
              data-testid={`mls-ma-cancel-btn-${req.request_id}`} onClick={() => onCancel(req)}>
              <XCircle className="w-3 h-3 mr-1" /> Cancel
            </Button>
          </div>
        )}
      </div>
      {history.length > 0 && (
        <div className="mt-2">
          <button type="button" onClick={() => setShowHistory((s) => !s)}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-blue-700"
            data-testid={`mls-ma-history-toggle-${req.request_id}`}>
            {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showHistory ? 'Hide' : 'View'} cycle history ({history.length})
          </button>
          {showHistory && <div className="mt-1.5 space-y-1.5">{history.map((h) => <HistoryRow key={h.request_id} req={h} onOpenAttachment={onOpenAttachment} />)}</div>}
        </div>
      )}
    </div>
  );
};

/** Group rows into cycle chains (latest first) via previous_request_id. */
export const buildChains = (requests) => {
  const byId = new Map(requests.map((r) => [r.request_id, r]));
  const superseded = new Set(requests.map((r) => r.previous_request_id).filter(Boolean));
  const heads = requests.filter((r) => !superseded.has(r.request_id));
  return heads
    .map((head) => { const chain = [head]; let cur = head; while (cur?.previous_request_id && byId.has(cur.previous_request_id)) { cur = byId.get(cur.previous_request_id); chain.push(cur); } return chain; })
    .sort((a, b) => String(b[0].created_at || '').localeCompare(String(a[0].created_at || '')));
};

export default function MlsMailAwayModal({ open, onOpenChange, orderId, requests = [], readOnly = false, onChanged, autoNew = false }) {
  const [sendMode, setSendMode] = useState(autoNew && !readOnly ? { mode: 'create' } : null);
  const [retrying, setRetrying] = useState(false);
  const retryEmail = async (req) => {
    if (retrying) return; // duplicate-click guard
    setRetrying(true);
    try {
      const res = await axios.post(`${API}/api/orders/${orderId}/components/mls/mail-away/${req.request_id}/email/retry`);
      const e = res.data?.email || {};
      if (res.data?.not_sent) toast.info(`Email to ${e.recipient} was NOT sent — ${e.not_sent_reason || 'live email delivery is not activated'}.`, { duration: 8000 });
      else if (res.data?.in_flight) toast.info(`An email for this batch is already being sent to ${e.recipient}.`);
      else toast.success(res.data?.already_sent ? `Email already sent to ${e.recipient}` : `Mail-Away email sent to ${e.recipient}`);
      onChanged?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Mail-Away email could not be sent'), { duration: 8000 });
      onChanged?.();
    } finally { setRetrying(false); }
  };
  const [receiveReq, setReceiveReq] = useState(null);
  const [cancelReq, setCancelReq] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [busy, setBusy] = useState(false);

  const chains = useMemo(() => buildChains(requests), [requests]);
  const activeCount = requests.filter((r) => MAIL_AWAY_ACTIVE.includes(r.status)).length;
  const overdueCount = requests.filter((r) => getEffectiveMailAwayStatus(r) === 'overdue').length;

  const doReceive = async (notes, files) => {
    setBusy(true);
    try {
      const attachments = await uploadMlsFiles(orderId, files);
      await axios.post(`${API}/api/orders/${orderId}/components/mls/mail-away/${receiveReq.request_id}/receive`, { response_note: notes || null, attachments });
      toast.success(`Response received — ${receiveReq.department_name}`);
      setReceiveReq(null); onChanged?.();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : (d?.message || 'Action failed')); }
    setBusy(false);
  };

  const doCancel = async (reason) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/orders/${orderId}/components/mls/mail-away/${cancelReq.request_id}/cancel`, { reason });
      toast.success(`Mail-Away cancelled — ${cancelReq.department_name}`);
      setCancelReq(null); onChanged?.();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : (d?.message || 'Action failed')); }
    setBusy(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl w-[calc(100vw-2rem)] max-h-[85vh] flex flex-col gap-0 p-0 overflow-hidden" data-testid="mls-mailaway-modal">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 pr-12">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-purple-100 rounded-full shrink-0"><Mail size={18} className="text-purple-600" /></div>
              <div className="min-w-0">
                <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                  MLS Mail-Away Requests
                  {activeCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200 font-semibold" data-testid="mls-ma-active-count">{activeCount} active — blocks MLS completion</span>}
                  {overdueCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200 font-semibold" data-testid="mls-ma-overdue-count">{overdueCount} overdue</span>}
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">Each request is emailed with its attachments to the client code’s Mail-Away Email.</DialogDescription>
              </div>
            </div>
            {!readOnly && (
              <Button type="button" size="sm" className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white shrink-0" data-testid="mls-ma-new-btn" onClick={() => setSendMode({ mode: 'create' })}>
                <Plus className="w-3.5 h-3.5 mr-1" /> New Request
              </Button>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-2 bg-slate-50/40">
            {chains.length === 0 && (
              <div className="text-center py-10 text-sm text-slate-400" data-testid="mls-ma-empty">
                No MLS Mail-Away requests yet.{!readOnly && ' Use “New Request” to track a request sent to a department.'}
              </div>
            )}
            {chains.map((chain) => (
              <RequestRow key={chain[0].request_id} chain={chain} readOnly={readOnly}
                onReceive={setReceiveReq} onResend={(r) => setSendMode({ mode: 'resend', previous: r })} onCancel={setCancelReq} onOpenAttachment={setAttachment}
                onRetryEmail={retryEmail} />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {sendMode && (
        <MlsMailAwaySendModal key={sendMode.previous?.request_id || 'new'} open orderId={orderId} mode={sendMode.mode} previous={sendMode.previous || null} existingRequests={requests}
          onDone={() => { setSendMode(null); onChanged?.(); }} onClose={() => setSendMode(null)} />
      )}
      {receiveReq && (
        <MailAwayResponseModal open targetName={receiveReq.department_name} loading={busy} onConfirm={doReceive} onClose={() => setReceiveReq(null)} testIdPrefix="mls-ma" />
      )}
      {cancelReq && (
        <MailAwayCancelModal open targetName={cancelReq.department_name} loading={busy} onConfirm={doCancel} onClose={() => setCancelReq(null)} testIdPrefix="mls-ma" />
      )}
      <MailAwayAttachmentViewer open={!!attachment} onOpenChange={(v) => { if (!v) setAttachment(null); }} orderId={orderId} attachment={attachment} />
    </>
  );
}
